/**
 * Floating Action Button (FAB) — Quick-access shortcuts
 * Shows contextual quick actions based on current page
 * Accessible: keyboard-navigable, aria-labels, focus-visible
 */

import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  X,
  FileText,
  ShoppingCart,
  Users,
  Package,
  Store,
  DollarSign,
} from 'lucide-react';

const QUICK_ACTIONS = [
  { icon: FileText,    label: 'فاتورة جديدة',    path: '/invoices/create',  color: 'bg-blue-500 hover:bg-blue-600' },
  { icon: Users,       label: 'عميل جديد',       path: '/customers',        color: 'bg-emerald-500 hover:bg-emerald-600' },
  { icon: Package,     label: 'منتج جديد',       path: '/products',         color: 'bg-violet-500 hover:bg-violet-600' },
  { icon: ShoppingCart,label: 'أمر شراء جديد',   path: '/purchase-orders',  color: 'bg-amber-500 hover:bg-amber-600' },
  { icon: Store,       label: 'فتح نقطة البيع',  path: '/pos',              color: 'bg-pink-500 hover:bg-pink-600' },
  { icon: DollarSign,  label: 'مصروف جديد',      path: '/expenses',         color: 'bg-rose-500 hover:bg-rose-600' },
];

/**
 * @param {object} props
 * @param {string} [props.className] – additional classes
 */
export const FloatingActionButton = ({ className = '' }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  return (
    <div
      ref={containerRef}
      className={`fixed bottom-6 left-6 z-50 flex flex-col-reverse items-center gap-3 ${className}`}
      dir="rtl"
    >
      {/* Quick action items */}
      {open && QUICK_ACTIONS.map((action, idx) => {
        const Icon = action.icon;
        return (
          <div
            key={action.path}
            className="flex items-center gap-2 animate-slide-up"
            style={{ animationDelay: `${idx * 40}ms` }}
          >
            {/* Label tooltip */}
            <span className="bg-slate-900/80 text-white text-xs px-2 py-1 rounded-lg whitespace-nowrap shadow-lg">
              {action.label}
            </span>
            <Link
              to={action.path}
              onClick={() => setOpen(false)}
              className={`w-11 h-11 rounded-full ${action.color} text-white shadow-lg flex items-center justify-center transition-all focus-ring`}
              aria-label={action.label}
            >
              <Icon size={18} />
            </Link>
          </div>
        );
      })}

      {/* Main FAB button */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={open ? 'إغلاق القائمة السريعة' : 'فتح القائمة السريعة'}
        className={`w-14 h-14 rounded-full bg-primary text-white shadow-xl flex items-center justify-center transition-all hover:bg-primary/90 focus-ring ${
          open ? 'rotate-45 scale-110' : ''
        }`}
      >
        {open ? <X size={24} /> : <Plus size={24} />}
      </button>
    </div>
  );
};

export default FloatingActionButton;
